import React from 'react';

export const Dashboard = React.lazy(() => import('@containers/dashboard'));
export const CloudAccounts = React.lazy(() => import('@containers/cloud-accounts'));
export const ScanHistory = React.lazy(() => import('@root/pages/history'));
export const NewScan = React.lazy(() => import('@root/pages/scanner'));
export const Compliance = React.lazy(() => import('@root/containers/compliance/index'));
export const ComplianceBreakdown = React.lazy(() => import('@root/containers/compliance/individual'));
export const Risk = React.lazy(() => import('@containers/risk'));

export const RiskTable = React.lazy(() => import('@containers/risk/table/RiskTable'));
export const Remediation = React.lazy(() => import('@containers/remediation'));
export const Exceptions = React.lazy(() => import('@containers/rules/exceptions'));
export const Onboarding = React.lazy(() => import('@containers/cloud-accounts/NoAccount'));
export const Environments = React.lazy(() => import('@root/containers/environment/index'));
export const ChangePassword = React.lazy(() => import('@containers/profile/changePassword'));
export const Notifications = React.lazy(() => import('@root/pages/notifications'));
export const RBACIndex = React.lazy(() => import('@containers/rbac'));
export const ConnectCloudAccount = React.lazy(() => import('@containers/cloud-accounts/NoAccount/Connect'));
export const Error = React.lazy(() => import('@components/error/404Screen'));
export const RulesNew = React.lazy(() => import('@root/pages/rules_new'));
export const RulesTable = React.lazy(() => import('@root/pages/rules_new/rulesTable'));
export const Profile = React.lazy(() => import('@root/pages/profile'));
export const AdminReports = React.lazy(() => import('@pages/admin'));
export const ScanReport2 = React.lazy(() => import('@pages/new-scan-report'));
export const IdentityNew = React.lazy(() => import('@pages/identity'));
export const DynamicDashboard = React.lazy(() => import('@pages/dynamic-dashboard'));
export const NewTemplateEditor = React.lazy(() => import('@pages/dynamic-dashboard/Editor/index'));

export const EventsOverview = React.lazy(() => import('@root/pages/events'));
export const Threat = React.lazy(() => import('@root/pages/threat'));
export const Workload = React.lazy(() => import('@root/pages/workload'));
export const Vulnerabilities = React.lazy(() => import('@root/pages/workloload-scanning'));
export const VulnerabilityManagement = React.lazy(() => import('@root/pages/vulnerability-management'));
export const RemediationReport = React.lazy(() => import('@root/containers/remediation-report'));
export const RemediationPolicy = React.lazy(() => import('@root/containers/remediation-policy'));
export const RemediationHub = React.lazy(() => import('@root/pages/remediation-hub'));
export const AttackSurface = React.lazy(() => import('@root/pages/attack-surface'));
export const DashboardNew = React.lazy(() => import('@root/containers/dashboard/DashboardNew'));
export const EventsTable = React.lazy(() => import('@root/pages/events/Table'));
export const SuppressedEvents = React.lazy(() => import('@root/pages/events/SuppressedEvents'));
// Single entry point for the Inventory module; the tab comes from the `tab` query param. The
// resources table is lazy-loaded by that shell as the `resources` tab, so it needs no entry here.
export const Inventory = React.lazy(() => import('@root/pages/inventory'));
export const CompliancePack = React.lazy(() => import('@root/components/compliancePack'));
export const MalwareOverview = React.lazy(() => import('@root/pages/Malware/Overview/index'));
export const MalwareOverviewNew = React.lazy(() => import('@root/pages/Malware/Overview/index-new'));
export const MalwareOverviewStatic = React.lazy(() => import('@root/pages/Malware/Overview/malware-static'));
// export const Findings = React.lazy(() => import('@root/pages/Malware/Findings/index'));

export const KubernetesOverview = React.lazy(() => import('@root/pages/kubernetes/index'));
export const KubernetesIdentity = React.lazy(() => import('@root/pages/kubernetes/pages/identity/index'));
export const KubernetesInventory = React.lazy(() => import('@root/pages/kubernetes/pages/inventory/index'));
export const KubernetesInventoryTable = React.lazy(() => import('@root/pages/kubernetes/pages/inventory/table'));
export const KubernetesCompliance = React.lazy(() => import('@root/pages/kubernetes/pages/compliance/index'));
export const KubernetesRuntime = React.lazy(() => import('@root/pages/kubernetes/pages/runtime/index'));
export const KubernetesSeccomp = React.lazy(() => import('@root/pages/kubernetes/pages/seccomp/index'));
export const KubernetesNetwork = React.lazy(() => import('@root/pages/kubernetes/pages/network/index'));
export const KubernetesRiskAcceptance = React.lazy(() => import('@root/pages/kubernetes/pages/risk-acceptance/index'));
export const KubernetesActionHistory = React.lazy(() => import('@root/pages/kubernetes/pages/action-history/index'));
export const K8Malware = React.lazy(() => import('@root/pages/kubernetes/pages/malware/index'));
export const KubernetesEnforcement = React.lazy(() => import('@root/pages/kubernetes/pages/enforcement/index'));
export const KubernetesImageScout = React.lazy(() => import('@root/pages/kubernetes/pages/image-scout/index'));
export const KubernetesScanHistory = React.lazy(() => import('@root/pages/kubernetes/pages/scan-history/index'));

export const CodeRegistry = React.lazy(() => import('@root/pages/code/registry/index'));
export const CodeRepo = React.lazy(() => import('@root/pages/code/repository/index'));
export const CodeRepoSkeleton = React.lazy(() => import('@root/pages/code/repository/skeleton'));
export const DastScanning = React.lazy(() => import('@root/pages/code/dast/index'));
export const ApplicationSecurity = React.lazy(() => import('@root/pages/application-security'));
export const DastEnvironmentDetails = React.lazy(() =>
  import('@root/pages/code/dast/environment-details/index'),
);

export const APISecurityOverview = React.lazy(() => import('@root/pages/code/api-security/index'));
export const APISecurityInventory = React.lazy(() => import('@root/pages/code/api-security/pages/inventory/index'));
export const APISecurityRisk = React.lazy(() => import('@root/pages/code/api-security/pages/risks/index'));
export const APISecurityShadowApis = React.lazy(
  () => import('@root/pages/code/api-security/pages/shadow-apis/index'),
);

export const DatabaseSecurityOverview = React.lazy(() => import('@root/pages/database-security/index'));
export const DatabaseSecurityModule = React.lazy(() => import('@root/pages/database-security/module'));

export const FinOps = React.lazy(() => import('@root/pages/finops'));
export const FinOpsAI = React.lazy(() => import('@root/pages/finops/FinOpsAI'));
export const CloudCostManagementStages = React.lazy(() => import('@root/pages/finops/CloudCostManagementStages'));

export const AISecurity = React.lazy(() => import('@root/pages/ai-security/index'));

export const WorkSpace = React.lazy(() => import('@root/pages/admin/workspace/index'));
export const RepositoryIntegration = React.lazy(
  () => import('@root/pages/notifications/pages/Channels/Configurations/RepositoryIntegration'),
);

export const BucketProtections = React.lazy(() => import('@root/pages/Malware/BucketProtection/index'));
export const WorkloadSkeleton = React.lazy(() => import('@root/pages/workloload-scanning/WorkloadSkeleton'));
export const PolicySkeleton = React.lazy(() => import('./../containers/remediation-policy/PolicySkeleton'));
export const ReportSkeleton = React.lazy(() => import('./../containers/remediation-report/ReportSkeleton'));
export const EventOverviewSkleton = React.lazy(() => import('./../pages/events/skeleton'));
export const EventTableSkleton = React.lazy(() => import('./../pages/events/Table/skeleton'));
export const RiskTableSkeleton = React.lazy(() => import('@root/containers/risk/table/skeleton'));
export const RiskSkeleton = React.lazy(() => import('./../containers/risk/skeleton'));
export const MalwareSkeleton = React.lazy(() => import('./../pages/Malware/Overview/skeleton-new'));
export const BucketTableSkleton = React.lazy(() => import('./../pages/Malware/BucketProtection/skeleton'));
// export const FindingTableSkleton = React.lazy(() => import('./../pages/Malware/Findings/skeleton'));
export const ResourceTableSkleton = React.lazy(
  () => import('./../pages/inventory-overview/resource-evaluation/skeleton'),
);
export const ResourceSkeleton = React.lazy(() => import('./../pages/inventory-overview/resources/skeleton'));
export const KeyInsightSkeleton = React.lazy(() => import('../pages/inventory-overviewNew/key-insights/skeleton'));
export const InventoryOverviewSkeleton = React.lazy(() => import('./../pages/inventory1.3/skeleton'));
export const DashboardSkeleton = React.lazy(() => import('./../containers/dashboard/skeleton'));
export const Explorer = React.lazy(() => import('@pages/explorer'));
// The Explorer graph (@pages/visualization) is lazy-loaded by the Inventory shell as its
// `?tab=explorer` tab, so it no longer needs a route-level entry here.
export const AttackPathAnalysis = React.lazy(() => import('@pages/attack-path-analysis'));
export const ViperAI = React.lazy(() => import('@pages/ViperAI/Dashboard'));
export const AIChat = React.lazy(() => import('@pages/ai-chat'));
export const Discovery = React.lazy(() => import('@pages/discovery'));
export const EOL = React.lazy(() => import('@pages/EOL'));
